import { CheckboxItemType } from '@wix/editor-platform-feature-store';
import { ActionableElement } from '@wix/editor-platform-manifest-services';
import { CompRef } from '@wix/document-services-types';
import { MetadataStore } from '../MetadataStore';
import { generateUniqueId } from '../utils';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import { IElementItemsBuilder, ManifestAPI } from '../types';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

const MAX_DEPTH = 3;

export class OOIElementItemsBuilder implements IElementItemsBuilder {
  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly metadataStore: MetadataStore,
    private readonly rootEditorPointer: EditorPointer<CompRef>,
  ) {}

  buildElementItems(
    elements: ActionableElement[],
    api: ManifestAPI,
    depth: number,
  ): CheckboxItemType[] {
    return elements.flatMap((element) =>
      this.#buildElementItem(element, api, depth),
    );
  }

  #buildElementItem(
    element: ActionableElement,
    _api: ManifestAPI,
    depth: number,
  ): CheckboxItemType[] {
    if (depth > MAX_DEPTH) {
      return [];
    }

    const elementEditorPointer = element.editorPointers[0];
    const children = element.elements.length
      ? this.buildElementItems(element.elements, _api, depth + 1)
      : [];

    if (!element.isActionable) {
      if (depth <= 2 && children.length > 1) {
        return [
          {
            displayName: element.displayName ?? '',
            id: generateUniqueId(this.editorPointerAPI, elementEditorPointer),
            isRemovable: false,
            children,
          },
        ];
      }
      return children;
    }

    const displayPropertyKey =
      this.editorPointerAPI.getInnerElementPath(elementEditorPointer)?.at(-1) ??
      CSS_PROPERTIES.CSS_PROPERTY_TYPE.display;

    const id = generateUniqueId(this.editorPointerAPI, elementEditorPointer);

    this.metadataStore.setItem(id, {
      editorPointer: elementEditorPointer,
      visibilityEditorPointer: this.rootEditorPointer,
      namespace: 'cssCustomProperties',
      key: displayPropertyKey,
      preservedVisibleStyleValue: undefined,
    });

    return [
      {
        displayName: element.displayName ?? '',
        id,
        isRemovable: true,
        children,
      },
    ];
  }
}
